import React from "react";
import {
  Smartphone, FileText, ClipboardCheck, ShieldCheck, Camera,
  Eye, Sparkles, Activity, Stethoscope, Users, Zap, Search,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, Lead, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  ParallelGrid, ChecklistCard, FeatureCallout, SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogMobileFirstPatientForms = () => (
  <RichBlogLayout
    category="Patient Experience"
    eyebrow="Patient Forms"
    title="Mobile-First Patient Forms: Why PDFs Are the #1 Reason Intake Breaks Down"
    subtitle="Forms designed for the way patients actually use phones — and prepopulated from prior visits so no one fills out medical history twice."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Lead>
      A new patient gets a PDF intake form. They open it on their phone in
      the car. The form is six pages, formatted for letter-sized paper,
      which means it fits on their screen at roughly the same size as a
      grain of rice. They pinch to zoom. They give up. They show up to
      their appointment with nothing filled out.
    </Lead>

    <Prose>
      <p>
        Patient intake is the most quietly broken workflow in dentistry.
        Almost every practice still uses some version of a PDF or a static
        web form designed for a desktop browser, and almost every patient
        tries to fill it out on a phone. The mismatch is the entire problem.
        Half-completed forms come back. Insurance card photos are blurry.
        Medical history is missing. The front desk re-enters everything
        anyway, because what came back wasn't usable.
      </p>
      <p>
        Asha replaces the PDF with mobile-first forms — designed
        specifically for how patients actually use their phones, and
        connected to the chart so prior answers pre-populate. The patient
        only updates what's changed. The data lands in the chart cleanly.
        Staff has nothing to retype.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 1, label: "PDF is the #1 reason intake breaks down" },
        { value: 6, label: "Pages of letter-sized paper crammed onto a phone screen" },
        { value: 0, label: "Fields the patient should refill from their last visit" },
        { value: 100, suffix: "%", label: "Forms that arrive ready to use, not raw text" },
      ]}
    />

    <Prose>
      <h2>The PDF is the problem, not the patient</h2>
      <p>
        It is tempting to blame patients for not completing forms. Most of
        them try. The PDF is what's broken — it assumes a keyboard, a mouse,
        and a printer, none of which the average patient has handy when they
        get the link. A form that requires zooming, rotating, and pinching is
        a form that gets abandoned at the third question.
      </p>
      <p>
        The downstream cost lands on the front desk. Forms don't come back.
        Reminder texts go out. The same forms still don't come back. The
        patient arrives, the form is half-done, and the front desk types
        the rest into the PMS while the patient sits in the waiting room.
        Nothing about this is the patient's fault. It's the format.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="PDF / static web forms"
      afterTitle="Asha mobile-first forms"
      beforeItems={[
        "Designed for letter paper, opened on a phone",
        "Patient pinches, zooms, abandons the third page",
        "Insurance card photo upload is awkward and often blurry",
        "Form data arrives as raw text — front desk re-types into the PMS",
      ]}
      afterItems={[
        "Built for mobile from the first pixel",
        "Clear blocks: chief complaint, medical history, consent, insurance",
        "Native camera capture for insurance cards with auto-extraction",
        "Data flows directly into structured chart fields — nothing to retype",
      ]}
    />

    <Prose>
      <h2>What the patient is actually asked for</h2>
      <p>
        Asha's intake forms aren't longer. They're better organized. The
        whole experience is broken into clear blocks that map to what the
        clinical team actually needs before the visit. Nothing extra,
        nothing confusing.
      </p>
    </Prose>

    <ParallelGrid
      title="The blocks of a mobile-first intake"
      columns={2}
      items={[
        { title: "Chief complaint", subtitle: "Why are you here today?", icon: Stethoscope, status: "active" },
        { title: "Medical history", subtitle: "Pre-populated from prior visits", icon: ClipboardCheck, status: "active" },
        { title: "Consent + signatures", subtitle: "Digital, mobile-friendly", icon: FileText, status: "active" },
        { title: "Insurance card photo", subtitle: "Native camera + auto-extract", icon: Camera, status: "active" },
      ]}
    />

    <Prose>
      <p>
        These four blocks are everything most new visits actually need, and
        each one is built for the device the patient has. Consent isn't a
        scroll-and-sign-with-your-finger ordeal at the bottom of a 1980s
        legal document. It's a clear card, a tap, a captured signature.
        Insurance isn't "type your member ID, group number, and plan name
        into three separate fields" — it's a photo of the card, parsed into
        structured fields, ready to verify.
      </p>

      <h2>Pre-population is the unlock</h2>
      <p>
        The single biggest cause of intake friction for returning patients
        is being asked the same medical history questions they answered six
        months ago. Asha refuses to do that. Anything the chart already
        knows is pre-filled, clearly marked as "from your prior visit," and
        the patient is asked one question: has anything changed?
      </p>
    </Prose>

    <ChecklistCard
      title="Maria L · returning visit · medical history"
      items={[
        { label: "Penicillin allergy — from 2025-08 visit", status: "pass" },
        { label: "Currently taking Atorvastatin — from 2025-08 visit", status: "pass" },
        { label: "No known cardiovascular conditions", status: "pass" },
        { label: "Pregnancy status — please confirm", status: "running" },
      ]}
    />

    <Prose>
      <p>
        Three of those four lines are answered before the patient even
        opens the form. Only the field with a real reason to be re-asked
        gets re-asked. The form takes 30 seconds instead of 10 minutes,
        and the patient doesn't have to remember what they took for high
        cholesterol or how their last appointment ended.
      </p>
    </Prose>

    <PullQuote>
      The patient only updates what's changed, not everything from scratch.
      That alone removes one of the biggest sources of frustration in
      intake.
    </PullQuote>

    <Prose>
      <h2>What staff sees before sending</h2>
      <p>
        Before any form goes out, staff can preview exactly what the patient
        will see — on a phone, in real conditions. There are no surprises in
        the experience. The block order, the question wording, the
        signature flow, the insurance camera capture: all visible before
        the link is sent.
      </p>
      <p>
        That preview is small but powerful. It catches the moments when a
        clinical addition to the form (a new screening, a temporary
        consent) might have shipped without anyone noticing how it looked
        on a small screen. Staff doesn't have to imagine the patient's
        experience. They see it.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Patient receives the form link",
          body:
            "Sent via SMS or email with no login required. Tap and start filling.",
        },
        {
          title: "Returning patient sees prior answers pre-populated",
          body:
            "Medical history, allergies, medications — all carried over from the last visit, clearly marked as needing confirmation only.",
        },
        {
          title: "Insurance card is captured by the phone's camera",
          body:
            "Member ID, group number, and plan are auto-extracted into structured fields. No typing.",
        },
        {
          title: "Consents and signatures are captured natively",
          body:
            "Each consent is a clear card with a touch signature. No PDF to rotate, no checkbox to hunt for.",
        },
        {
          title: "Data flows into the chart in structured form",
          body:
            "Front desk has nothing to transcribe. Clinical team sees a clean, ready-to-use intake before the patient sits down.",
        },
      ]}
    />

    <Prose>
      <h2>What completion rates actually look like</h2>
      <p>
        The reason mobile-first matters isn't elegance. It's completion. PDF
        intake forms in dentistry have completion rates that hover in the
        embarrassing range — somewhere between "half the patients" and
        "most of the patients," depending on the practice. Mobile-first
        forms with pre-population push that closer to "almost everyone,
        before they get to the parking lot."
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 50, suffix: "%", label: "Typical PDF intake completion before arrival" },
        { value: 90, suffix: "%+", label: "Mobile-first completion before arrival" },
        { value: 30, suffix: "s", label: "Average time for a returning patient to refresh history" },
        { value: 0, label: "Fields the front desk has to retype" },
      ]}
    />

    <Prose>
      <p>
        That gap — from 50% to 90%+ — is the difference between a front
        desk that spends the morning chasing forms and a front desk that
        spends it on patients. Multiply that gap by the number of new and
        returning visits a busy practice handles each day, and the time
        savings show up everywhere on the daily schedule.
      </p>

      <h2>Where this lands clinically</h2>
      <p>
        For the clinical team, mobile-first forms with pre-population mean
        the chart is actually accurate before the appointment. Allergies,
        medications, and conditions aren't a guess. The hygienist isn't
        having to re-ask things the patient already documented. The doctor
        isn't relying on the patient remembering correctly mid-procedure.
      </p>
      <p>
        It also means the consent record is clean. Every signature is
        timestamped, attached to the chart, and tied to the version of the
        consent the patient saw. Audit-ready, without a single sheet of
        paper to file.
      </p>
    </Prose>

    <FeatureCallout
      stat="90%+"
      label="Forms completed before the patient ever sits in the chair"
      description="Mobile-first design and chart-aware pre-population make intake the part of the visit that's already done."
      icon={Smartphone}
    />

    <Prose>
      <h2>What disappears from the front desk's day</h2>
      <p>
        The front desk's intake routine quietly shrinks. No more chasing
        unsigned forms. No more squinting at insurance card photos that
        came in sideways. No more retyping medical history that was already
        on file last August. The patient walks in, the chart is current,
        the visit starts on time.
      </p>
      <p>
        It is the rare workflow that gets better simultaneously for the
        patient and the staff, with no compromise. Patients prefer the
        experience because it respects their time and their phone. Staff
        prefers it because the work they used to do — the typing, the
        chasing, the cleaning up — is the work they're no longer doing.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        PDFs were never going to survive contact with a smartphone, and
        the data shows it. Mobile-first forms with chart-aware
        pre-population turn intake from the most-hated front desk job into
        a step that's done before the patient walks in — accurately,
        cleanly, and without anyone retyping a single answer.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogMobileFirstPatientForms;
